import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { ExpenseClaimApiService } from '../../../core/api-services/expense-claim-api.service';
import { ExpenseApiService } from '../../../core/api-services/expense-api.service';
import { ClaimDocumentInput, ClaimDetail, ClaimSummary } from '../../../core/models/expense-claim';
import { ExpenseCategory } from '../../../core/models/expense';

/**
 * Student expense claims.
 *
 * A claim is raised against one of the student's awarded benefits and must carry
 * at least one supporting document. If a reviewer rejects it, the student uploads
 * a new document (and corrects the amount if needed) and resubmits it for review.
 */
@Component({
  selector: 'app-student-claims',
  standalone: true,
  imports: [CommonModule, DatePipe, ReactiveFormsModule],
  templateUrl: './claims.html',
  styleUrl: './claims.scss',
})
export class StudentClaimsComponent implements OnInit {
  private readonly claimsApi = inject(ExpenseClaimApiService);
  private readonly expenseApi = inject(ExpenseApiService);
  private readonly fb = inject(FormBuilder);

  readonly claims = signal<ClaimSummary[]>([]);
  readonly categories = signal<ExpenseCategory[]>([]);
  readonly selected = signal<ClaimDetail | null>(null);
  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly message = signal('');

  /** Files uploaded for the claim currently being submitted or resubmitted. */
  readonly pendingDocuments = signal<ClaimDocumentInput[]>([]);
  readonly uploading = signal(false);

  readonly showForm = signal(false);
  readonly showResubmit = signal(false);

  readonly claimForm = this.fb.group({
    scholarshipAwardBenefitId: [null as number | null, Validators.required],
    title: ['', Validators.required],
    description: [''],
    amount: [null as number | null, [Validators.required, Validators.min(0.01)]],
    dateIncurred: ['', Validators.required],
  });

  readonly resubmitForm = this.fb.group({
    amount: [null as number | null],
    description: [''],
  });

  ngOnInit(): void {
    this.load();
    this.loadCategories();
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');

    this.claimsApi
      .getMyClaims()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (claims) => this.claims.set(claims ?? []),
        error: () => this.error.set('Your claims could not be loaded.'),
      });
  }

  private loadCategories(): void {
    this.expenseApi.getMyExpenses().subscribe({
      next: (overview) =>
        this.categories.set((overview.categories ?? []).filter((category) => category.canClaim)),
      error: () => this.categories.set([]),
    });
  }

  openForm(): void {
    this.message.set('');
    this.error.set('');
    this.pendingDocuments.set([]);
    this.claimForm.reset({ scholarshipAwardBenefitId: null, amount: null, dateIncurred: '' });
    this.showForm.set(true);
  }

  closeForm(): void {
    this.showForm.set(false);
  }

  openDetail(claim: ClaimSummary): void {
    this.error.set('');
    this.claimsApi.getMyClaim(claim.id).subscribe({
      next: (detail) => this.selected.set(detail),
      error: () => this.error.set('This claim could not be loaded.'),
    });
  }

  closeDetail(): void {
    this.selected.set(null);
    this.showResubmit.set(false);
    this.pendingDocuments.set([]);
  }

  /** Uploads a document immediately so its object key can be attached. */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    this.uploading.set(true);
    this.error.set('');

    this.claimsApi
      .uploadDocument(file)
      .pipe(finalize(() => this.uploading.set(false)))
      .subscribe({
        next: (document) => {
          this.pendingDocuments.update((documents) => [...documents, document]);
          input.value = '';
        },
        error: (err) => this.error.set(err?.error?.message || 'The document could not be uploaded.'),
      });
  }

  removeDocument(objectKey: string): void {
    this.pendingDocuments.update((documents) =>
      documents.filter((document) => document.objectKey !== objectKey)
    );
  }

  submit(): void {
    if (this.claimForm.invalid || this.busy()) {
      this.claimForm.markAllAsTouched();
      return;
    }

    if (this.pendingDocuments().length === 0) {
      this.error.set('Attach at least one supporting document before submitting.');
      return;
    }

    const value = this.claimForm.getRawValue();
    this.busy.set(true);
    this.error.set('');
    this.message.set('');

    this.claimsApi
      .submitClaim({
        scholarshipAwardBenefitId: value.scholarshipAwardBenefitId!,
        title: value.title ?? '',
        description: value.description || null,
        amount: value.amount!,
        dateIncurred: value.dateIncurred ?? '',
        documents: this.pendingDocuments(),
      })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (claim) => {
          this.message.set(`Claim ${claim.referenceNumber} submitted. Reviewers have been notified.`);
          this.pendingDocuments.set([]);
          this.showForm.set(false);
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The claim could not be submitted.'),
      });
  }

  startResubmit(claim: ClaimDetail): void {
    this.selected.set(claim);
    this.pendingDocuments.set([]);
    this.resubmitForm.reset({ amount: claim.amount, description: claim.description ?? '' });
    this.showResubmit.set(true);
  }

  resubmit(): void {
    const claim = this.selected();
    if (!claim || this.busy()) {
      return;
    }

    if (this.pendingDocuments().length === 0) {
      this.error.set('Attach at least one new document with the resubmission.');
      return;
    }

    const value = this.resubmitForm.getRawValue();
    this.busy.set(true);
    this.error.set('');
    this.message.set('');

    this.claimsApi
      .resubmitClaim(claim.id, {
        amount: value.amount ?? null,
        description: value.description || null,
        documents: this.pendingDocuments(),
      })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (updated) => {
          this.message.set(`Claim ${updated.referenceNumber} resubmitted for review.`);
          this.selected.set(updated);
          this.pendingDocuments.set([]);
          this.showResubmit.set(false);
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The claim could not be resubmitted.'),
      });
  }

  cancel(claim: ClaimSummary): void {
    this.error.set('');
    this.message.set('');

    this.claimsApi.cancelClaim(claim.id).subscribe({
      next: () => {
        this.message.set('Claim withdrawn.');
        this.closeDetail();
        this.load();
      },
      error: (err) => this.error.set(err?.error?.message || 'The claim could not be withdrawn.'),
    });
  }

  download(documentId: number): void {
    const claim = this.selected();
    if (!claim) {
      return;
    }

    this.claimsApi.downloadMyDocument(claim.id, documentId).subscribe({
      next: (blob) => this.openBlob(blob, `claim-${claim.referenceNumber}-${documentId}`),
      error: () => this.error.set('The document could not be downloaded.'),
    });
  }

  private openBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const anchor = window.document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    window.document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }

  canWithdraw(claim: ClaimSummary): boolean {
    return claim.status === 'SUBMITTED' || claim.status === 'RESUBMITTED';
  }

  canResubmit(claim: ClaimSummary): boolean {
    return claim.status === 'REJECTED';
  }

  statusClass(status: string): string {
    switch (status) {
      case 'APPROVED':
      case 'PAID':
        return 'status status-approved';
      case 'REJECTED':
        return 'status status-rejected';
      case 'CANCELLED':
        return 'status status-cancelled';
      case 'RESUBMITTED':
        return 'status status-booked';
      default:
        return 'status status-pending';
    }
  }
}
